/**
 * Жизненный цикл слоёв на сцене.
 *
 * Отвечает на один вопрос: какие слои сейчас должны существовать
 * в DOM, а какие — нет.
 *
 * ПРАВИЛО: виджет создаётся, когда его прямоугольник ПЕРЕСЕКАЕТСЯ
 * с кадром хотя бы частично. Не «лежит внутри целиком» — иначе
 * сломался бы выезд из-за края, а это законный приём: слой был бы
 * не создан, пока не въедет полностью, и зритель увидел бы, как он
 * возникает из ниоткуда посреди экрана.
 *
 * Обратная сторона правила важнее. Рабочая область больше кадра
 * (задача 2.2), и за его пределами модератор держит наготове то,
 * что вот-вот покажет. Такой слой НЕ создаётся: картинка за кадром
 * ничего не стоит, а видео продолжало бы декодироваться и звук
 * играть — съедая процессор стримера ровно во время эфира. Вместо
 * создания идёт предзагрузка: появиться должно мгновенно, а до тех
 * пор не стоить ничего.
 */
import { intersectsFrame, } from './contract.js';
/**
 * Host держит DOM сцены в соответствии со списком слоёв.
 */
export class Host {
    root;
    defs = new Map();
    live = new Map();
    /** Слои, для которых уже сделана предзагрузка: повторять незачем. */
    preloaded = new Set();
    onError;
    mountAll;
    stats = {
        mounted: 0,
        updated: 0,
        destroyed: 0,
        preloaded: 0,
        failed: 0,
    };
    constructor(options) {
        this.root = options.root;
        this.onError = options.onError ?? (() => { });
        this.mountAll = options.mountAll ?? false;
        for (const def of options.widgets) {
            this.defs.set(def.type, def);
        }
    }
    /**
     * Приводит DOM в соответствие со сценой.
     *
     * Вызывается на каждое изменение — и на снапшот, и на дельту.
     * Разница между ними здесь не нужна: хост сравнивает желаемое
     * с текущим сам, и одна реализация вместо двух не разойдётся.
     */
    sync(layers, frame) {
        const wanted = new Set();
        for (const layer of layers) {
            const visible = this.mountAll ||
                (!layer.hidden &&
                    (layer.keepMounted === true || intersectsFrame(layer.rect, frame.w, frame.h)));
            if (!visible) {
                // За кадром или спрятан: не создаём, но готовим тяжёлое
                this.preload(layer);
                continue;
            }
            wanted.add(layer.id);
            this.place(layer);
        }
        // Всё, чего в списке больше нет или что ушло за кадр, — уничтожаем.
        // Именно уничтожаем, а не прячем: спрятанное видео продолжает
        // декодироваться и играть
        for (const [id, live] of this.live) {
            if (!wanted.has(id)) {
                this.destroy(id, live);
            }
        }
    }
    /**
     * Пересоздаёт узел одного слоя.
     *
     * Нужно эмбедам: чужая страница умеет зависать — чат потерял
     * сокет, алерты перестали приходить, — и изнутри мы этого
     * не видим и починить не можем. Единственное доступное действие
     * — создать фрейм заново.
     *
     * ИМЕННО ПЕРЕСОЗДАНИЕ, А НЕ update. Обычный путь бережёт узлы
     * ровно затем, чтобы фрейм НЕ перезагружался; здесь нужно
     * обратное, и другого способа нет: перезагрузить чужой документ
     * снаружи нельзя.
     *
     * ОСТАЛЬНЫЕ СЛОИ НЕ ТРОГАЮТСЯ — это и есть смысл метода.
     * Пересоздать всю сцену было бы проще, но тогда одна залипшая
     * рамка стоила бы перезапуска соседнего видео и потери
     * накопленной истории у соседнего чата.
     *
     * Неизвестный слой — не ошибка: его могли удалить, пока просьба
     * шла по сети. Возвращает, случилось ли что-нибудь, — вызывающему
     * это нужно для лога, больше никому.
     */
    reload(id) {
        const live = this.live.get(id);
        if (live === undefined)
            return false;
        // Место в дереве запоминается ДО уничтожения: порядок узлов
        // задаёт z-index, но соседи с равным z различаются только
        // позицией в разметке, и слой, дописанный в конец, оказался бы
        // поверх них
        const before = live.instance.el.nextSibling;
        live.instance.destroy();
        const instance = live.def.mount(live.props);
        this.live.set(id, { ...live, instance });
        this.stats.mounted += 1;
        this.position(instance.el, live.layer);
        // insertBefore, а не append: с null во втором доводе он и есть
        // append, и отдельной ветки «слой был последним» не нужно
        this.root.insertBefore(instance.el, before);
        return true;
    }
    /**
     * Передаёт слою команду воспроизведения.
     *
     * Возвращает, нашлось ли кому её выполнить. Ответ нужен вызывающему
     * для журнала: слой могли удалить, пока команда шла по сети, а вид
     * слоя может не уметь играть вовсе — и то и другое не ошибка.
     */
    command(id, name, positionMs) {
        const live = this.live.get(id);
        if (live === undefined)
            return false;
        const run = live.instance.command;
        if (run === undefined)
            return false;
        run.call(live.instance, name, positionMs);
        return true;
    }
    /**
     * Приводит воспроизведение слоя к общему состоянию.
     *
     * Возвращает, нашёлся ли слой: отсутствие — обычное дело, слой мог
     * быть за кадром и не создан вовсе.
     */
    playback(id, target) {
        const live = this.live.get(id);
        if (live === undefined)
            return false;
        const sync = live.instance.syncPlayback;
        if (sync === undefined)
            return false;
        sync.call(live.instance, target);
        return true;
    }
    /** Уничтожает всё: вызывается при смене сцены и при выгрузке. */
    clear() {
        for (const [id, live] of this.live) {
            this.destroy(id, live);
        }
        this.preloaded.clear();
    }
    place(layer) {
        const def = this.defs.get(layer.type);
        if (def === undefined) {
            // Неизвестный тип — не повод падать: остальная сцена должна
            // работать. Но и молчать нельзя, иначе слой просто не появится
            // и никто не поймёт почему
            this.fail(layer.id, new Error(`неизвестный тип слоя: ${layer.type}`));
            return;
        }
        const existing = this.live.get(layer.id);
        /*
          БЫСТРЫЙ ПУТЬ: ГЕОМЕТРИЯ БЕЗ РАЗБОРА СВОЙСТВ.
    
          Пока модератор тянет слой, оверлей пересобирает сцену на каждом
          кадре жеста — и каждому слою разбирались свойства и вызывался
          update, хотя менялся прямоугольник одного. Тридцать слоёв
          в кадре это тридцать разборов и тридцать обновлений шестьдесят
          раз в секунду.
    
          Свойства приходят из состояния сцены и между её правками
          остаются ТЕМ ЖЕ объектом, поэтому их неизменность видна
          по ссылке. Разбор — чистая функция от них: второй вызов дал бы
          то же самое, а update с теми же свойствами ничего не меняет.
        */
        if (existing !== undefined &&
            existing.type === layer.type &&
            existing.layer.props === layer.props) {
            this.position(existing.instance.el, layer);
            // Слой всё равно перезаписывается: перезагрузка обязана
            // пересоздать то, что видно сейчас, а видно уже новое место
            this.live.set(layer.id, { ...existing, layer });
            return;
        }
        let props;
        try {
            props = def.parse(layer.props);
        }
        catch (error) {
            // Один кривой слой не должен ронять рендер оверлея у всех
            // зрителей канала: остальные слои продолжают жить
            this.fail(layer.id, error);
            return;
        }
        if (existing !== undefined && existing.type === layer.type) {
            existing.instance.update(props);
            this.stats.updated += 1;
            this.position(existing.instance.el, layer);
            // Слой перезаписывается: перезагрузка обязана пересоздать
            // то, что видно сейчас, а не то, что было при создании
            this.live.set(layer.id, { ...existing, layer, props });
            return;
        }
        if (existing !== undefined) {
            // Тип сменился: тот же идентификатор, другой виджет. Обновить
            // нельзя — у них разные узлы и разные свойства
            this.destroy(layer.id, existing);
        }
        const instance = def.mount(props);
        this.live.set(layer.id, { def, instance, type: layer.type, layer, props });
        this.stats.mounted += 1;
        this.position(instance.el, layer);
        this.root.append(instance.el);
    }
    preload(layer) {
        if (this.preloaded.has(layer.id)) {
            return;
        }
        const def = this.defs.get(layer.type);
        if (def?.preload === undefined) {
            return;
        }
        let props;
        try {
            props = def.parse(layer.props);
        }
        catch {
            // Молча: негодные свойства припаркованного слоя всплывут
            // при выводе его в кадр, и сообщать об этом дважды незачем
            return;
        }
        def.preload(props);
        this.preloaded.add(layer.id);
        this.stats.preloaded += 1;
    }
    destroy(id, live) {
        live.instance.destroy();
        this.live.delete(id);
        this.preloaded.delete(id);
        this.stats.destroyed += 1;
    }
    fail(id, error) {
        this.stats.failed += 1;
        this.onError(id, error);
    }
    /**
     * Раскладывает узел по координатам сцены.
     *
     * translate3d, а не left/top: смена left вызывает пересчёт
     * раскладки всей страницы, а трансформация — только композитинг.
     * При шестидесяти обновлениях в секунду разница между ними — это
     * разница между плавным движением и рывками (docs/01).
     */
    position(el, layer) {
        /* eslint-disable no-param-reassign --
           Правило бережёт неизменяемость СОСТОЯНИЯ, а здесь меняется DOM —
           ровно то, ради чего метод и существует. Требование
           иммутабельности относится к сцене, а не к узлам, которыми
           её рисуют. Тот же случай, что в apps/overlay/src/render.ts. */
        el.dataset.layerId = layer.id;
        /*
          В СТИЛЬ ПИШЕТСЯ ТОЛЬКО ИЗМЕНИВШЕЕСЯ.
    
          Раскладка зовётся на КАЖДЫЙ слой при каждой пересборке сцены,
          а сцена пересобирается на каждом кадре чужого жеста. Пока тянут
          один слой, все остальные получали те же семь записей шестьдесят
          раз в секунду. Присвоение того же значения браузер чаще всего
          пропускает сам, но путь до этого решения проходит через разбор
          строки CSS — а таких путей в кадре по семь на слой.
    
          Сравнение с инлайновым стилем, а не со своей копией: копия —
          это второй источник правды о том, что сейчас в узле, и она
          разошлась бы с ним при первой же правке снаружи.
        */
        setStyle(el, 'position', 'absolute');
        setStyle(el, 'left', '0');
        setStyle(el, 'top', '0');
        setStyle(el, 'width', `${layer.rect.w}px`);
        setStyle(el, 'height', `${layer.rect.h}px`);
        // Поворот — частью той же трансформации, а не отдельным
        // свойством: браузер применяет её на видеокарте одним шагом,
        // а два свойства заставили бы пересчитывать раскладку
        const turn = layer.rotation ? ` rotate(${layer.rotation}deg)` : '';
        /*
          ОТРАЖЕНИЕ ИДЁТ ПОСЛЕДНИМ В СТРОКЕ — то есть применяется
          К СЛОЮ ПЕРВЫМ, до поворота. Порядок здесь не косметика:
          отрази повёрнутый слой — и он уедет в сторону, а одно и то же
          нажатие давало бы разный результат в зависимости от того,
          поворачивали слой до или после.
    
          Центр общий у обоих: transform-origin по умолчанию — середина
          узла, и поворот с отражением крутятся вокруг одной точки.
        */
        const mirror = mirrorOf(layer);
        setStyle(el, 'transform', `translate3d(${layer.rect.x}px, ${layer.rect.y}px, 0)${turn}${mirror}`);
        setStyle(el, 'zIndex', String(layer.z));
        /* eslint-enable no-param-reassign */
    }
    /** Сколько слоёв сейчас живо. Только для тестов и отладки. */
    get size() {
        return this.live.size;
    }
}
function setStyle(el, prop, value) {
    if (el.style[prop] === value)
        return;
    // eslint-disable-next-line no-param-reassign -- меняется DOM, а не состояние
    el.style[prop] = value;
}
/**
 * Часть трансформации, отвечающая за зеркало.
 *
 * Пустая строка, когда отражать нечего: лишний `scale(1, 1)`
 * в каждом слое ничего не меняет на экране, но заставляет браузер
 * держать у узла матрицу вместо простого сдвига — а слоёв в кадре
 * десятки, и трогаются они по кадру.
 */
function mirrorOf(layer) {
    if (!layer.flipH && !layer.flipV)
        return '';
    return ` scale(${layer.flipH ? -1 : 1}, ${layer.flipV ? -1 : 1})`;
}
//# sourceMappingURL=host.js.map